import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { Pencil, Trash2, Plus, Upload, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { MultiLanguageInput } from "@/components/admin/MultiLanguageInput";
import { MultiLanguageTextarea } from "@/components/admin/MultiLanguageTextarea";
import { MultiLanguageArrayInput } from "@/components/admin/MultiLanguageArrayInput";

const AdminDestinations = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [editingDestination, setEditingDestination] = useState<any>(null);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<"es" | "en" | "fr">("es");

  const { data: destinations, isLoading } = useQuery({
    queryKey: ["admin-destinations"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("destinations")
        .select("*")
        .order("name");
      if (error) throw error;
      return data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("destinations").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-destinations"] });
      toast({ title: "Destino eliminado correctamente" });
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (destination: any) => {
      if (destination.id) {
        const { error } = await supabase
          .from("destinations")
          .update(destination)
          .eq("id", destination.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("destinations").insert([destination]);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-destinations"] });
      toast({ title: "Destino guardado correctamente" });
      setIsEditing(false);
      setEditingDestination(null);
    },
  });

  const handleEdit = (destination: any) => {
    setEditingDestination(destination);
    setIsEditing(true);
  };

  const handleNew = () => {
    setEditingDestination({
      name: "",
      name_en: "",
      name_fr: "",
      slug: "",
      country: "",
      country_en: "",
      country_fr: "",
      short_description: "",
      short_description_en: "",
      short_description_fr: "",
      full_description: "",
      full_description_en: "",
      full_description_fr: "",
      gallery_images: [],
      highlights: [],
      highlights_en: [],
      highlights_fr: [],
      best_time_to_visit: "",
      best_time_to_visit_en: "",
      best_time_to_visit_fr: "",
      is_active: true,
    });
    setIsEditing(true);
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploadingImages(true);
    const uploadedUrls: string[] = [];

    try {
      for (const file of Array.from(files)) {
        // Validate file size (5MB max)
        if (file.size > 5242880) {
          toast({ 
            title: "Error", 
            description: `${file.name} excede el tamaño máximo de 5MB`,
            variant: "destructive"
          });
          continue;
        }

        // Generate unique filename
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = `${fileName}`;

        // Upload to Supabase Storage
        const { error: uploadError } = await supabase.storage
          .from('destination-images')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) throw uploadError;

        // Get public URL
        const { data: { publicUrl } } = supabase.storage
          .from('destination-images')
          .getPublicUrl(filePath);

        uploadedUrls.push(publicUrl);
      }

      // Update gallery_images array
      const currentImages = editingDestination.gallery_images || [];
      setEditingDestination({
        ...editingDestination,
        gallery_images: [...currentImages, ...uploadedUrls]
      });

      toast({ title: `${uploadedUrls.length} imagen(es) subida(s) correctamente` });
    } catch (error) {
      console.error('Error uploading images:', error);
      toast({ 
        title: "Error al subir imágenes", 
        description: "Por favor intenta de nuevo",
        variant: "destructive"
      });
    } finally {
      setUploadingImages(false);
      event.target.value = '';
    }
  };

  const handleRemoveImage = (indexToRemove: number) => {
    const updatedImages = editingDestination.gallery_images.filter(
      (_: string, index: number) => index !== indexToRemove
    );
    setEditingDestination({
      ...editingDestination,
      gallery_images: updatedImages
    });
  };

  if (isEditing && editingDestination) {
    return (
      <div className="max-w-4xl mx-auto">
        <Card>
          <CardHeader>
            <div className="flex justify-between items-start">
              <CardTitle>{editingDestination.id ? "Editar Destino" : "Nuevo Destino"}</CardTitle>
              <div className="flex gap-2">
                <Button
                  variant={selectedLanguage === "es" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedLanguage("es")}
                >
                  🇪🇸 ES
                </Button>
                <Button
                  variant={selectedLanguage === "en" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedLanguage("en")}
                >
                  🇬🇧 EN
                </Button>
                <Button
                  variant={selectedLanguage === "fr" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedLanguage("fr")}
                >
                  🇫🇷 FR
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Nombre</Label>
              <Input
                value={
                  selectedLanguage === "es"
                    ? editingDestination.name || ""
                    : selectedLanguage === "en"
                    ? editingDestination.name_en || ""
                    : editingDestination.name_fr || ""
                }
                onChange={(e) =>
                  setEditingDestination({
                    ...editingDestination,
                    [selectedLanguage === "es" ? "name" : selectedLanguage === "en" ? "name_en" : "name_fr"]: e.target.value,
                  })
                }
              />
            </div>
            <div>
              <Label>Slug</Label>
              <Input
                value={editingDestination.slug}
                onChange={(e) => setEditingDestination({ ...editingDestination, slug: e.target.value })}
              />
            </div>
            <div>
              <Label>País</Label>
              <Input
                value={
                  selectedLanguage === "es"
                    ? editingDestination.country || ""
                    : selectedLanguage === "en"
                    ? editingDestination.country_en || ""
                    : editingDestination.country_fr || ""
                }
                onChange={(e) =>
                  setEditingDestination({
                    ...editingDestination,
                    [selectedLanguage === "es" ? "country" : selectedLanguage === "en" ? "country_en" : "country_fr"]: e.target.value,
                  })
                }
              />
            </div>
            <div className="space-y-3">
              <Label>Galería de imágenes</Label>
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  id="image-upload"
                  multiple
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={handleImageUpload}
                  disabled={uploadingImages}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => document.getElementById('image-upload')?.click()}
                  disabled={uploadingImages}
                >
                  <Upload className="mr-2 h-4 w-4" />
                  {uploadingImages ? "Subiendo..." : "Subir imágenes"}
                </Button>
                <span className="text-sm text-muted-foreground">
                  Máx. 5MB por imagen (JPEG, PNG, WebP)
                </span>
              </div>
              
              {editingDestination.gallery_images && editingDestination.gallery_images.length > 0 && (
                <div className="grid grid-cols-3 gap-4 mt-4">
                  {editingDestination.gallery_images.map((imageUrl: string, index: number) => (
                    <div key={index} className="relative group">
                      <img
                        src={imageUrl}
                        alt={`Gallery ${index + 1}`}
                        className="w-full h-32 object-cover rounded-md"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute top-2 right-2 h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => handleRemoveImage(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label>Mejor época para visitar</Label>
              <Input
                value={
                  selectedLanguage === "es"
                    ? editingDestination.best_time_to_visit || ""
                    : selectedLanguage === "en"
                    ? editingDestination.best_time_to_visit_en || ""
                    : editingDestination.best_time_to_visit_fr || ""
                }
                onChange={(e) =>
                  setEditingDestination({
                    ...editingDestination,
                    [selectedLanguage === "es" ? "best_time_to_visit" : selectedLanguage === "en" ? "best_time_to_visit_en" : "best_time_to_visit_fr"]: e.target.value,
                  })
                }
              />
            </div>
            <div>
              <Label>Destacados</Label>
              <div className="space-y-2">
                {(selectedLanguage === "es"
                  ? editingDestination.highlights || []
                  : selectedLanguage === "en"
                  ? editingDestination.highlights_en || []
                  : editingDestination.highlights_fr || []
                ).map((highlight: string, index: number) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={highlight}
                      onChange={(e) => {
                        const field = selectedLanguage === "es" ? "highlights" : selectedLanguage === "en" ? "highlights_en" : "highlights_fr";
                        const newHighlights = [...(editingDestination[field] || [])];
                        newHighlights[index] = e.target.value;
                        setEditingDestination({ ...editingDestination, [field]: newHighlights });
                      }}
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      onClick={() => {
                        const field = selectedLanguage === "es" ? "highlights" : selectedLanguage === "en" ? "highlights_en" : "highlights_fr";
                        const newHighlights = (editingDestination[field] || []).filter((_: string, i: number) => i !== index);
                        setEditingDestination({ ...editingDestination, [field]: newHighlights });
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const field = selectedLanguage === "es" ? "highlights" : selectedLanguage === "en" ? "highlights_en" : "highlights_fr";
                    setEditingDestination({
                      ...editingDestination,
                      [field]: [...(editingDestination[field] || []), ""],
                    });
                  }}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Agregar destacado
                </Button>
              </div>
            </div>
            <div>
              <Label>Descripción corta</Label>
              <textarea
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                rows={3}
                value={
                  selectedLanguage === "es"
                    ? editingDestination.short_description || ""
                    : selectedLanguage === "en"
                    ? editingDestination.short_description_en || ""
                    : editingDestination.short_description_fr || ""
                }
                onChange={(e) =>
                  setEditingDestination({
                    ...editingDestination,
                    [selectedLanguage === "es" ? "short_description" : selectedLanguage === "en" ? "short_description_en" : "short_description_fr"]: e.target.value,
                  })
                }
              />
            </div>
            <div>
              <Label>Descripción completa</Label>
              <textarea
                className="flex min-h-[200px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                rows={8}
                value={
                  selectedLanguage === "es"
                    ? editingDestination.full_description || ""
                    : selectedLanguage === "en"
                    ? editingDestination.full_description_en || ""
                    : editingDestination.full_description_fr || ""
                }
                onChange={(e) =>
                  setEditingDestination({
                    ...editingDestination,
                    [selectedLanguage === "es" ? "full_description" : selectedLanguage === "en" ? "full_description_en" : "full_description_fr"]: e.target.value,
                  })
                }
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={editingDestination.is_active}
                onChange={(e) => setEditingDestination({ ...editingDestination, is_active: e.target.checked })}
              />
              <Label>Activo</Label>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => saveMutation.mutate(editingDestination)}>Guardar</Button>
              <Button variant="outline" onClick={() => setIsEditing(false)}>
                Cancelar
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Gestión de Destinos</h1>
        <Button onClick={handleNew}>
          <Plus className="mr-2 h-4 w-4" />
          Nuevo Destino
        </Button>
      </div>

      {isLoading ? (
        <div>Cargando...</div>
      ) : (
        <div className="grid gap-4">
          {destinations?.map((destination) => (
            <Card key={destination.id}>
              <CardContent className="pt-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-xl font-semibold">{destination.name}</h3>
                      <Badge>{destination.country}</Badge>
                      {!destination.is_active && <Badge variant="secondary">Inactivo</Badge>}
                    </div>
                    <p className="text-muted-foreground mb-2">{destination.short_description}</p>
                    {destination.best_time_to_visit && (
                      <div className="text-sm text-muted-foreground">
                        Mejor época: {destination.best_time_to_visit}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="icon" onClick={() => handleEdit(destination)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="destructive"
                      size="icon"
                      onClick={() => deleteMutation.mutate(destination.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminDestinations;
